import React, { useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { Bold, Italic, Underline, Code, Trash2, Type, Heading, Image, Square } from 'lucide-react';

/** Palette items that can be dragged into the design canvas */
const PALETTE_ITEMS: { id: string; label: string; html: string; icon: React.ReactNode }[] = [
  { id: 'text', label: 'Text', html: '<p style="margin:0 0 16px;color:#495057;font-size:16px;">New paragraph</p>', icon: <Type className="w-4 h-4" /> },
  { id: 'heading', label: 'Heading', html: '<h2 style="margin:0 0 16px;font-size:24px;font-weight:600;color:#1a1a1a;">Heading</h2>', icon: <Heading className="w-4 h-4" /> },
  { id: 'button', label: 'Button', html: '<a href="#" style="display:inline-block;background:#000;color:#fff;padding:14px 32px;text-decoration:none;font-weight:600;font-size:14px;line-height:1.35;text-align:center;vertical-align:middle;box-sizing:border-box;border-radius:4px;">Button</a>', icon: <Square className="w-4 h-4" /> },
  { id: 'image', label: 'Image', html: '<img src="https://via.placeholder.com/200x80?text=Image" alt="Image" style="max-width:100%;height:auto;" />', icon: <Image className="w-4 h-4" /> },
];

export interface SelectedElementInfo {
  tagName: string;
  textContent: string;
  src?: string;
  alt?: string;
  href?: string;
  target?: string;
  style: {
    color?: string;
    fontSize?: string;
    backgroundColor?: string;
    fontFamily?: string;
    fontWeight?: string;
    width?: string;
    height?: string;
    borderRadius?: string;
  };
}

export interface EmailTemplateDesignEditorHandle {
  undo: () => void;
  canUndo: () => boolean;
}

interface EmailTemplateDesignEditorProps {
  /** Full HTML document string (with body content; variables already replaced for display) */
  previewHtml: string;
  onHtmlChange: (bodyInnerHtml: string) => void;
  /** Increment when switching from Code to Design so iframe re-mounts with latest content */
  resetKey?: number;
  /** Called when undo availability changes (e.g. for toolbar button disabled state) */
  onUndoAvailability?: (canUndo: boolean) => void;
}

const EDITABLE_SELECTOR = [
  'a', 'button', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'td', 'span', 'li', 'img', 'code', 'pre',
  'div[style]',
  'div[class]:not(.email-wrapper):not(.email-container):not(.content):not(.header):not(.footer):not(.logo):not(.footer-links)',
].join(', ');

function buildDesignDocument(bodyContent: string): string {
  const script = `
(function() {
  var lastSelected = null;
  var HIGHLIGHT_OUTLINE = 'none';
  var HIGHLIGHT_OFFSET = '0';
  var DESIGN_DND_DEBUG = false;
  function dndDebug() {
    if (!DESIGN_DND_DEBUG || !window.console || !console.log) return;
    try {
      var args = Array.prototype.slice.call(arguments);
      args.unshift('[design-dnd]');
      console.log.apply(console, args);
    } catch (e) {}
  }
  function describeNode(el) {
    if (!el || !el.tagName) return '(null)';
    var tag = el.tagName.toLowerCase();
    var cls = '';
    if (el.className && typeof el.className === 'string') {
      cls = '.' + el.className.trim().replace(/\\s+/g, '.');
    }
    var txt = '';
    if (el.textContent && typeof el.textContent === 'string') {
      txt = el.textContent.trim().slice(0, 30);
    }
    return tag + cls + (txt ? ' "' + txt + '"' : '');
  }

  var EDITABLE_SEL = '${EDITABLE_SELECTOR.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}';
  function findEditableElement(el) {
    if (!el || el === document.body) return null;
    if (el.matches && el.matches(EDITABLE_SEL)) return el;
    return findEditableElement(el.parentElement);
  }
  var draggedDesignElement = null;
  var pendingPaletteHtml = null;

  function getComputedStyleMap(el) {
    var s = window.getComputedStyle(el);
    var out = {
      color: s.color || '',
      fontSize: s.fontSize || '',
      backgroundColor: s.backgroundColor || '',
      fontFamily: s.fontFamily || '',
      fontWeight: s.fontWeight || '',
      width: el.style.width || s.width || '',
      height: el.style.height || s.height || '',
      borderRadius: el.style.borderRadius || s.borderRadius || ''
    };
    if (el.tagName && el.tagName.toLowerCase() === 'img') {
      return { width: out.width, height: out.height, borderRadius: out.borderRadius };
    }
    return out;
  }

  var moveOffsetX = 0;
  var moveOffsetY = 0;
  function clearSelection() {
    if (lastSelected) {
      lastSelected.style.outline = '';
      lastSelected.style.outlineOffset = '';
      lastSelected.removeAttribute('data-design-selected');
      if (lastSelected.getAttribute('contenteditable') === 'true') {
        lastSelected.removeAttribute('contenteditable');
        lastSelected.blur();
      }
      lastSelected = null;
    }
    moveOffsetX = 0;
    moveOffsetY = 0;
  }

  function getBodyHeight() {
    return document.body.scrollHeight || document.body.offsetHeight || 0;
  }
  function cleanupDesignArtifacts(root) {
    if (!root || !root.querySelectorAll) return;
    try {
      root.querySelectorAll('[data-design-drop-indicator]').forEach(function(el) {
        if (el && el.parentNode) el.parentNode.removeChild(el);
      });
      root.querySelectorAll('[data-design-selected]').forEach(function(el) {
        el.removeAttribute('data-design-selected');
      });
      root.querySelectorAll('[style]').forEach(function(el) {
        if (!el || !el.style) return;
        try {
          // Never persist editor chrome styles to template HTML.
          if (el.style.outline) el.style.outline = '';
          if (el.style.outlineOffset) el.style.outlineOffset = '';
          if (el.style.pointerEvents === 'none') el.style.pointerEvents = '';
          var raw = (el.getAttribute('style') || '').toLowerCase();
          var looksLikeOldDropIndicator =
            raw.indexOf('rgba(14,165,233') !== -1 ||
            raw.indexOf('rgba(14, 165, 233') !== -1 ||
            (raw.indexOf('height:4px') !== -1 && raw.indexOf('box-shadow:0 0 0 1px') !== -1);
          var looksLikeSelectionBlue =
            raw.indexOf('#3b82f6') !== -1 ||
            raw.indexOf('rgb(59,130,246)') !== -1 ||
            raw.indexOf('rgb(59, 130, 246)') !== -1;
          if (looksLikeOldDropIndicator && !el.textContent.trim()) {
            if (el.parentNode) el.parentNode.removeChild(el);
          }
          // Strip accidental persisted selection chrome from legacy saves.
          if (looksLikeSelectionBlue) {
            if (el.style.border) el.style.border = '';
            if (el.style.borderColor) el.style.borderColor = '';
            if (el.style.boxShadow && (el.style.boxShadow.indexOf('59, 130, 246') >= 0 || el.style.boxShadow.indexOf('59,130,246') >= 0)) {
              el.style.boxShadow = '';
            }
          }
          // Collapse oversized wrappers around a single button/link caused by previous drag artifacts.
          var tag = el.tagName ? el.tagName.toLowerCase() : '';
          if (tag === 'p' || tag === 'div') {
            var buttonKids = el.querySelectorAll('a,button');
            if (buttonKids.length === 1) {
              var ownText = '';
              for (var ti = 0; ti < el.childNodes.length; ti++) {
                var tn = el.childNodes[ti];
                if (tn.nodeType === 3) ownText += tn.textContent || '';
              }
              if (!ownText.trim()) {
                if (el.style.height) el.style.height = '';
                if (el.style.minHeight) el.style.minHeight = '';
              }
            }
          }
        } catch (e2) {}
      });
    } catch (e) {}
  }
  function serializeAndSend() {
    cleanupDesignArtifacts(document.body);
    var clone = document.body.cloneNode(true);
    cleanupDesignArtifacts(clone);
    window.parent.postMessage({ type: 'design-serialized', html: clone.innerHTML, bodyHeight: getBodyHeight() }, '*');
  }
  function sendContentHeight() {
    window.parent.postMessage({ type: 'design-content-height', bodyHeight: getBodyHeight() }, '*');
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sendContentHeight);
  } else {
    sendContentHeight();
  }

  function getDropZone() {
    return document.querySelector('.email-container') || document.querySelector('.content') || document.querySelector('[class*="content"]') || document.body;
  }
  function isInsideDropZone(el) {
    if (!el) return false;
    var zone = getDropZone();
    return zone === el || zone.contains(el);
  }
  function setDraggableOnEditables() {
    var zone = getDropZone();
    if (!zone) return;
    try {
      zone.querySelectorAll(EDITABLE_SEL).forEach(function(el) { el.setAttribute('draggable', 'true'); });
    } catch (e) {}
  }
  /** Hit-test without landing on the node being dragged (otherwise insert position is null and the block "jumps"). */
  function elementFromPointExcluding(clientX, clientY, excludeEl) {
    var offs = [[0,0],[16,0],[-16,0],[0,16],[0,-16],[24,0],[-24,0],[0,24],[0,-24],[16,16],[-16,-16],[16,-16],[-16,16],[32,0],[-32,0]];
    for (var i = 0; i < offs.length; i++) {
      var t = document.elementFromPoint(clientX + offs[i][0], clientY + offs[i][1]);
      if (!t || !isInsideDropZone(t)) continue;
      if (!excludeEl || (t !== excludeEl && !excludeEl.contains(t))) {
        dndDebug('hit', { x: clientX, y: clientY, offset: offs[i], target: describeNode(t), exclude: describeNode(excludeEl) });
        return t;
      }
    }
    dndDebug('hit-none', { x: clientX, y: clientY, exclude: describeNode(excludeEl) });
    return null;
  }
  /** If hit is a full-width block, prefer nearby inline children so drag between row items works. */
  function pickLayoutTargetForInsert(editable, clientX, clientY) {
    if (!editable || !editable.querySelectorAll) return editable;
    var tag = editable.tagName && editable.tagName.toLowerCase();
    var isBlock =
      tag === 'p' || tag === 'h1' || tag === 'h2' || tag === 'h3' || tag === 'h4' ||
      (tag === 'div' && editable.classList && editable.classList.contains('content'));
    if (!isBlock) return editable;
    var nodes = editable.querySelectorAll('a,button,img');
    var best = null;
    var bestDx = Infinity;
    for (var j = 0; j < nodes.length; j++) {
      var n = nodes[j];
      var r = n.getBoundingClientRect();
      if (clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom) {
        return n;
      }
      var inRowBand = clientY >= (r.top - 18) && clientY <= (r.bottom + 18);
      if (inRowBand) {
        var dx = Math.abs((r.left + r.width / 2) - clientX);
        if (dx < bestDx) {
          bestDx = dx;
          best = n;
        }
      }
    }
    if (best && bestDx <= 180) return best;
    return editable;
  }
  function getNearestEditableInZone(zone, clientX, clientY, excludeEl, draggedEl) {
    if (!zone || !zone.querySelectorAll) return null;
    var nodes = zone.querySelectorAll(EDITABLE_SEL);
    var best = null, bestScore = Infinity;
    var bestInline = null, bestInlineScore = Infinity;
    var draggedTag = draggedEl && draggedEl.tagName ? draggedEl.tagName.toLowerCase() : '';
    var draggingInline = draggedTag === 'a' || draggedTag === 'button' || draggedTag === 'img' || draggedTag === 'span';
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (!el || el === excludeEl || (excludeEl && excludeEl.contains(el))) continue;
      var r = el.getBoundingClientRect();
      if (r.width <= 1 || r.height <= 1) continue;
      var cx = r.left + r.width / 2;
      var cy = r.top + r.height / 2;
      var dx = Math.abs(clientX - cx);
      var dy = Math.abs(clientY - cy);
      var tag = el.tagName ? el.tagName.toLowerCase() : '';
      var isInlineLike = tag === 'a' || tag === 'button' || tag === 'img' || tag === 'span';
      var nearSameRow = clientY >= (r.top - 24) && clientY <= (r.bottom + 24);
      var score = (dy * 2.6) + dx;
      if (score < bestScore) {
        bestScore = score;
        best = el;
      }
      if (draggingInline && isInlineLike && nearSameRow) {
        var inlineScore = (dy * 0.8) + dx;
        if (inlineScore < bestInlineScore) {
          bestInlineScore = inlineScore;
          bestInline = el;
        }
      }
    }
    if (bestInline && bestInlineScore <= 320) return bestInline;
    // Avoid snapping to very far elements (e.g., footer) if pointer is in free space.
    if (best && bestScore <= 280) return best;
    return null;
  }
  /** Pick insert position from pointer: full-width blocks use top/bottom; narrow blocks use the stronger axis (nx vs ny) so stacked vs side-by-side both work. */
  function getInsertBeforeFromRect(el, clientX, clientY) {
    var parent = el.parentNode;
    if (!parent) return null;
    var rect = el.getBoundingClientRect();
    var midY = rect.top + rect.height / 2;
    var midX = rect.left + rect.width / 2;
    var parentWidth = parent.getBoundingClientRect().width;
    var isFullWidth = rect.width >= parentWidth * 0.82;
    var before;
    var indicator = 'horizontal';
    if (isFullWidth) {
      if (clientY <= midY) before = el;
      else before = el.nextSibling;
    } else {
      var nx = Math.abs(clientX - midX) / Math.max(rect.width / 2, 1);
      var ny = Math.abs(clientY - midY) / Math.max(rect.height / 2, 1);
      if (ny > nx) {
        if (clientY <= midY) before = el;
        else before = el.nextSibling;
      } else {
        indicator = 'vertical';
        if (clientX < midX) before = el;
        else before = el.nextSibling;
      }
    }
    dndDebug('insert-from-rect', {
      on: describeNode(el),
      parent: describeNode(parent),
      before: describeNode(before),
      x: clientX,
      y: clientY,
      isFullWidth: isFullWidth,
      indicator: indicator
    });
    return { parent: parent, before: before, indicator: indicator };
  }
  function getInsertBeforeNode(zone, clientX, clientY, excludeEl) {
    if (!zone) return null;
    var target = elementFromPointExcluding(clientX, clientY, excludeEl);
    if (!target || !isInsideDropZone(target) || target === zone) {
      dndDebug('insert-at-zone-end', { x: clientX, y: clientY, zone: describeNode(zone) });
      return { parent: zone, before: null, indicator: 'horizontal' };
    }
    var editable = findEditableElement(target);
    if (editable && editable !== excludeEl && (!excludeEl || !excludeEl.contains(editable)) && editable.parentNode && isInsideDropZone(editable.parentNode)) {
      dndDebug('editable-hit', { target: describeNode(target), editable: describeNode(editable) });
      editable = pickLayoutTargetForInsert(editable, clientX, clientY);
      return getInsertBeforeFromRect(editable, clientX, clientY);
    }
    if (zone.contains(target) && target !== excludeEl && (!excludeEl || !excludeEl.contains(target))) {
      var p = target.parentNode;
      if (p && isInsideDropZone(p) && target.nodeType === 1) {
        dndDebug('fallback-target', { target: describeNode(target), parent: describeNode(p) });
        return getInsertBeforeFromRect(target, clientX, clientY);
      }
    }
    dndDebug('fallback-zone-end', { target: describeNode(target), zone: describeNode(zone) });
    return { parent: zone, before: null, indicator: 'horizontal' };
  }
  function insertHtmlAt(html, clientX, clientY) {
    var wrap = document.createElement('div');
    wrap.innerHTML = html.trim();
    var toInsert = wrap.firstChild;
    if (!toInsert) return;
    var zone = getDropZone();
    var pos = getInsertBeforeNode(zone, clientX, clientY, null);
    // Palette insertions: if generic logic resolves to container end, try a local row anchor
    // so the new button does not jump to top/footer when dropped in whitespace between items.
    if (pos && pos.parent === zone && !pos.before) {
      var nearest = getNearestEditableInZone(zone, clientX, clientY, null, toInsert);
      if (nearest && nearest.parentNode && isInsideDropZone(nearest.parentNode)) {
        nearest = pickLayoutTargetForInsert(nearest, clientX, clientY);
        var nearPos = getInsertBeforeFromRect(nearest, clientX, clientY);
        if (nearPos) pos = nearPos;
      }
    }
    var parent = pos ? pos.parent : zone;
    var before = pos ? pos.before : null;
    if (before) {
      parent.insertBefore(toInsert, before);
    } else {
      parent.appendChild(toInsert);
    }
    setDraggableOnEditables();
    sendContentHeight();
    serializeAndSend();
  }
  function moveElementTo(el, clientX, clientY) {
    var zone = getDropZone();
    if (!zone || !el || !el.parentNode) return;
    var pos = getInsertBeforeNode(zone, clientX, clientY, el);
    if (!pos) {
      dndDebug('move-no-pos', { el: describeNode(el), x: clientX, y: clientY });
      return;
    }
    dndDebug('move', { el: describeNode(el), parent: describeNode(pos.parent), before: describeNode(pos.before), x: clientX, y: clientY });
    if (pos.before) {
      pos.parent.insertBefore(el, pos.before);
    } else {
      pos.parent.appendChild(el);
    }
    sendContentHeight();
    serializeAndSend();
  }
  document.body.addEventListener('dragstart', function(e) {
    var editable = findEditableElement(e.target);
    if (!editable || !isInsideDropZone(editable)) return;
    draggedDesignElement = editable;
    dndDebug('dragstart', { el: describeNode(editable) });
    try { if (editable.style) editable.style.pointerEvents = 'none'; } catch (err) {}
    e.dataTransfer.setData('text/plain', 'design-move');
    e.dataTransfer.effectAllowed = 'move';
  }, true);
  document.body.addEventListener('dragend', function(e) {
    var el = draggedDesignElement;
    dndDebug('dragend', { el: describeNode(el) });
    try { if (el && el.style) el.style.pointerEvents = ''; } catch (err2) {}
    draggedDesignElement = null;
    removeDropIndicator();
  }, true);
  var lastDropX = 0;
  var lastDropY = 0;
  var dropIndicatorEl = null;
  function removeDropIndicator() {
    if (dropIndicatorEl && dropIndicatorEl.parentNode) {
      dropIndicatorEl.parentNode.removeChild(dropIndicatorEl);
      dropIndicatorEl = null;
    }
  }
  function showDropIndicator(clientX, clientY) {
    removeDropIndicator();
  }
  document.body.addEventListener('dragover', function(e) {
    var target = document.elementFromPoint(e.clientX, e.clientY);
    if (!isInsideDropZone(target)) {
      removeDropIndicator();
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    if (draggedDesignElement && (target === draggedDesignElement || draggedDesignElement.contains(target))) {
      e.preventDefault();
      e.stopImmediatePropagation();
      e.dataTransfer.dropEffect = 'move';
      return;
    }
    lastDropX = e.clientX;
    lastDropY = e.clientY;
    e.preventDefault();
    e.stopImmediatePropagation();
    e.dataTransfer.dropEffect = draggedDesignElement ? 'move' : 'copy';
  }, true);
  document.body.addEventListener('dragleave', function(e) {
    var rel = e.relatedTarget;
    if (rel && document.body.contains(rel)) return;
    removeDropIndicator();
  }, true);
  document.body.addEventListener('dragenter', function(e) {
    var target = document.elementFromPoint(e.clientX, e.clientY);
    if (isInsideDropZone(target)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      lastDropX = e.clientX;
      lastDropY = e.clientY;
    }
  }, true);
  var dropHandledAt = 0;
  var dropHandledInThisGesture = false;
  document.body.addEventListener('drop', function(e) {
    var target = document.elementFromPoint(e.clientX, e.clientY);
    if (!isInsideDropZone(target)) {
      removeDropIndicator();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    removeDropIndicator();
    if (dropHandledInThisGesture) return;
    var now = Date.now();
    if (now - dropHandledAt < 1500) return;
    dropHandledAt = now;
    dropHandledInThisGesture = true;
    setTimeout(function() { dropHandledInThisGesture = false; }, 500);
    var x = lastDropX;
    var y = lastDropY;
    var raw = e.dataTransfer.getData('text/plain');
    dndDebug('drop', { x: x, y: y, raw: raw, dragged: describeNode(draggedDesignElement) });
    if (raw === 'design-move' && draggedDesignElement) {
      moveElementTo(draggedDesignElement, x, y);
      draggedDesignElement = null;
      return;
    }
    if (pendingPaletteHtml) {
      var html = pendingPaletteHtml;
      pendingPaletteHtml = null;
      insertHtmlAt(html, x, y);
      return;
    }
  }, true);
  function initDraggables() {
    cleanupDesignArtifacts(document.body);
    setDraggableOnEditables();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDraggables);
  } else {
    initDraggables();
  }
  window.addEventListener('load', initDraggables);

  document.body.addEventListener('click', function(e) {
    e.preventDefault();
    e.stopPropagation();
    var target = findEditableElement(e.target);
    if (!target) {
      clearSelection();
      window.parent.postMessage({ type: 'design-select', element: null }, '*');
      return;
    }
    clearSelection();
    lastSelected = target;
    var tx = target.style.transform;
    if (tx && typeof tx === 'string') {
      var m = tx.match(/translate\\(\\s*([^,\\s)]+)px\\s*,\\s*([^)\\s]+)px\\s*\\)/);
      if (m) {
        moveOffsetX = parseFloat(m[1]) || 0;
        moveOffsetY = parseFloat(m[2]) || 0;
      }
    }
    target.setAttribute('data-design-selected', 'true');
    target.style.outline = HIGHLIGHT_OUTLINE;
    target.style.outlineOffset = HIGHLIGHT_OFFSET;
    var isImg = target.tagName && target.tagName.toLowerCase() === 'img';
    var isAnchor = target.tagName && target.tagName.toLowerCase() === 'a';
    if (!isImg) {
      target.setAttribute('contenteditable', 'true');
      target.focus();
    } else {
      if (!document.body.hasAttribute('tabindex')) document.body.setAttribute('tabindex', '-1');
      document.body.focus();
    }
    var payload = {
      tagName: target.tagName.toLowerCase(),
      textContent: (isImg ? '' : (target.textContent || '')),
      style: getComputedStyleMap(target)
    };
    if (isImg) {
      payload.src = target.src || target.getAttribute('src') || '';
      payload.alt = target.getAttribute('alt') || '';
    }
    if (isAnchor) {
      payload.href = target.getAttribute('href') || '';
      payload.target = target.getAttribute('target') || '';
    }
    window.parent.postMessage({ type: 'design-select', element: payload }, '*');
  }, true);

  function getElementSiblings(el) {
    var out = [];
    var s = el.parentNode ? el.parentNode.firstChild : null;
    while (s) {
      if (s.nodeType === 1 && s !== el) out.push(s);
      s = s.nextSibling;
    }
    return out;
  }
  function rectsOverlap(a, b, pad) {
    if (!pad) pad = 2;
    return !(a.right - pad < b.left + pad || a.left + pad > b.right - pad || a.bottom - pad < b.top + pad || a.top + pad > b.bottom - pad);
  }
  /** Intersection width / min(width) — high when elements share a column (stacked flow). */
  function horizontalOverlapRatio(a, b) {
    var left = Math.max(a.left, b.left);
    var right = Math.min(a.right, b.right);
    var w = Math.max(0, right - left);
    var minW = Math.min(a.width, b.width);
    if (minW <= 0) return 0;
    return w / minW;
  }
  /** Intersection height / min(height) — high when elements share a row (side-by-side). */
  function verticalOverlapRatio(a, b) {
    var top = Math.max(a.top, b.top);
    var bottom = Math.min(a.bottom, b.bottom);
    var h = Math.max(0, bottom - top);
    var minH = Math.min(a.height, b.height);
    if (minH <= 0) return 0;
    return h / minH;
  }
  document.addEventListener('keydown', function(e) {
    if (!lastSelected || !lastSelected.parentNode || !e.altKey) return;
    if (!isInsideDropZone(lastSelected)) return;
    var key = e.key;
    var step = 8;
    var dx = 0, dy = 0;
    if (key === 'ArrowUp') dy = -step;
    else if (key === 'ArrowDown') dy = step;
    else if (key === 'ArrowLeft') dx = -step;
    else if (key === 'ArrowRight') dx = step;
    else return;
    e.preventDefault();
    e.stopPropagation();
    moveOffsetX += dx;
    moveOffsetY += dy;
    lastSelected.style.transform = 'translate(' + moveOffsetX + 'px,' + moveOffsetY + 'px)';
    var selRect = lastSelected.getBoundingClientRect();
    var siblings = getElementSiblings(lastSelected);
    var parent = lastSelected.parentNode;
    var collided = null;
    var insertBefore = null;
    for (var i = 0; i < siblings.length; i++) {
      var s = siblings[i];
      var sRect = s.getBoundingClientRect();
      if (!rectsOverlap(selRect, sRect, 4)) continue;
      if (key === 'ArrowUp' || key === 'ArrowDown') {
        if (horizontalOverlapRatio(selRect, sRect) < 0.28) continue;
      } else {
        if (verticalOverlapRatio(selRect, sRect) < 0.28) continue;
      }
      if (key === 'ArrowUp' || key === 'ArrowLeft') {
        if (s.compareDocumentPosition(lastSelected) & document.DOCUMENT_POSITION_FOLLOWING) {
          collided = s;
          insertBefore = s;
          break;
        }
      } else {
        if (s.compareDocumentPosition(lastSelected) & document.DOCUMENT_POSITION_PRECEDING) {
          collided = s;
          insertBefore = s.nextSibling;
          break;
        }
      }
    }
    if (collided !== null && insertBefore !== undefined) {
      parent.insertBefore(lastSelected, insertBefore);
      moveOffsetX = 0;
      moveOffsetY = 0;
      lastSelected.style.transform = '';
      setDraggableOnEditables();
      sendContentHeight();
      serializeAndSend();
      window.parent.postMessage({ type: 'design-select', element: getElementPayload(lastSelected) }, '*');
    } else {
      sendContentHeight();
      serializeAndSend();
    }
  }, true);

  function getElementPayload(el) {
    var p = { tagName: el.tagName.toLowerCase(), textContent: el.textContent || '', style: getComputedStyleMap(el) };
    if (el.tagName && el.tagName.toLowerCase() === 'a') {
      p.href = el.getAttribute('href') || '';
      p.target = el.getAttribute('target') || '';
    }
    return p;
  }

  document.body.addEventListener('blur', function(e) {
    var el = e.target;
    if (el && el.getAttribute && el.getAttribute('data-design-selected') === 'true' && el.getAttribute('contenteditable') === 'true') {
      el.removeAttribute('contenteditable');
      serializeAndSend();
      window.parent.postMessage({ type: 'design-select', element: getElementPayload(el) }, '*');
    }
  }, true);

  document.body.addEventListener('input', function(e) {
    var el = e.target;
    if (el && el.getAttribute && el.getAttribute('data-design-selected') === 'true') {
      window.parent.postMessage({ type: 'design-select', element: getElementPayload(el) }, '*');
    }
  }, true);

  window.addEventListener('message', function(event) {
    var msg = event.data;
    if (msg.type === 'design-set-text' && lastSelected) {
      lastSelected.textContent = msg.value;
      serializeAndSend();
    } else if (msg.type === 'design-set-style' && lastSelected) {
      var s = lastSelected.style;
      if (msg.style && typeof msg.style === 'object') {
        if (msg.style.color !== undefined) s.color = msg.style.color;
        if (msg.style.fontSize !== undefined) s.fontSize = msg.style.fontSize;
        if (msg.style.backgroundColor !== undefined) s.backgroundColor = msg.style.backgroundColor;
        if (msg.style.fontFamily !== undefined) s.fontFamily = msg.style.fontFamily;
        if (msg.style.fontWeight !== undefined) s.fontWeight = msg.style.fontWeight;
        if (msg.style.width !== undefined) s.width = msg.style.width;
        if (msg.style.height !== undefined) s.height = msg.style.height;
        if (msg.style.borderRadius !== undefined) s.borderRadius = msg.style.borderRadius;
      }
      serializeAndSend();
    } else if (msg.type === 'design-set-img' && lastSelected && lastSelected.tagName && lastSelected.tagName.toLowerCase() === 'img') {
      if (msg.src !== undefined) lastSelected.src = msg.src;
      if (msg.alt !== undefined) lastSelected.setAttribute('alt', msg.alt);
      if (msg.width !== undefined) lastSelected.style.width = msg.width;
      if (msg.height !== undefined) lastSelected.style.height = msg.height;
      if (msg.borderRadius !== undefined) lastSelected.style.borderRadius = msg.borderRadius;
      serializeAndSend();
    } else if (msg.type === 'design-set-link' && lastSelected && lastSelected.tagName && lastSelected.tagName.toLowerCase() === 'a') {
      if (msg.href !== undefined) lastSelected.setAttribute('href', msg.href);
      if (msg.target !== undefined) { if (msg.target) lastSelected.setAttribute('target', msg.target); else lastSelected.removeAttribute('target'); }
      serializeAndSend();
    } else if (msg.type === 'design-get-serialized') {
      serializeAndSend();
    } else if (msg.type === 'design-palette-drag-start' && msg.html && typeof msg.html === 'string') {
      pendingPaletteHtml = msg.html;
    } else if (msg.type === 'design-palette-drag-end') {
      pendingPaletteHtml = null;
    } else if (msg.type === 'design-insert-at' && msg.html && typeof msg.html === 'string') {
      var now = Date.now();
      if (typeof window.__lastDesignInsertAt === 'number' && (now - window.__lastDesignInsertAt) < 300) return;
      window.__lastDesignInsertAt = now;
      insertHtmlAt(msg.html, msg.clientX || 0, msg.clientY || 0);
    } else if (msg.type === 'design-remove' && lastSelected && lastSelected.parentNode) {
      lastSelected.parentNode.removeChild(lastSelected);
      clearSelection();
      serializeAndSend();
      window.parent.postMessage({ type: 'design-select', element: null }, '*');
    } else if (msg.type === 'design-format' && lastSelected && lastSelected.tagName && lastSelected.tagName.toLowerCase() !== 'img') {
      if (msg.format === 'code') {
        var codeStyle = 'font-family: Monaco, \\'Courier New\\', monospace; background: #f5f5f5; padding: 2px 6px; border-radius: 2px;';
        var inner = lastSelected.innerHTML;
        lastSelected.innerHTML = '<code style="' + codeStyle + '">' + inner + '</code>';
        serializeAndSend();
      } else if (msg.format === 'bold' || msg.format === 'italic' || msg.format === 'underline') {
        var s = lastSelected.style;
        if (msg.format === 'bold') s.fontWeight = s.fontWeight === 'bold' ? '' : 'bold';
        if (msg.format === 'italic') s.fontStyle = s.fontStyle === 'italic' ? '' : 'italic';
        if (msg.format === 'underline') s.textDecoration = s.textDecoration === 'underline' ? '' : 'underline';
        serializeAndSend();
      } else {
        lastSelected.focus();
        var r = document.createRange();
        r.selectNodeContents(lastSelected);
        var sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(r);
        try { document.execCommand(msg.format, false, null); } catch (e) {}
        serializeAndSend();
      }
    }
  });
})();
`;
  const closeScript = '</scr' + 'ipt>'; // eslint-disable-line no-useless-concat
  const cleanBody = bodyContent.replace(/\s*data-design-selected="[^"]*"/gi, '');
  const googleFontsUrl = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;600&family=Lato:wght@400;700&family=Merriweather:wght@400;700&family=Montserrat:wght@400;600&family=Nunito:wght@400;600&family=Open+Sans:wght@400;600;700&family=Oswald:wght@400;600&family=Playfair+Display:wght@400;600&family=Poppins:wght@400;600&family=PT+Sans:wght@400;700&family=Raleway:wght@400;600&family=Roboto:wght@400;500;700&family=Source+Sans+3:wght@400;600&family=Ubuntu:wght@400;500&family=Work+Sans:wght@400;600&display=swap';
  return `<html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"><link href="${googleFontsUrl}" rel="stylesheet"><style>* { outline: none; } body { margin: 0; padding: 0; overflow-x: hidden; cursor: default; } img[src*="{{"] { min-height: 32px; background: #f1f5f9; object-fit: contain; } .content > * { margin-bottom: 12px; } .content > *:last-child { margin-bottom: 0; } .content > * + * { margin-top: 4px; }</style></head><body>${cleanBody}</body><script>${script}${closeScript}</html>`;
}

function extractBodyContent(previewHtml: string): string {
  const bodyMatch = previewHtml.match(/<body[^>]*>([\s\S]*)<\/body>/i);
  if (bodyMatch) return bodyMatch[1];
  return previewHtml;
}

const SYSTEM_FONT = '-apple-system, "system-ui", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
const FONT_OPTIONS: { value: string; label: string }[] = [
  { value: SYSTEM_FONT, label: 'System default' },
  { value: '"Open Sans", sans-serif', label: 'Open Sans' },
  { value: 'Roboto, sans-serif', label: 'Roboto' },
  { value: 'Lato, sans-serif', label: 'Lato' },
  { value: 'Montserrat, sans-serif', label: 'Montserrat' },
  { value: 'Poppins, sans-serif', label: 'Poppins' },
  { value: '__custom__', label: 'More Google Fonts…' },
];

const GOOGLE_FONTS_PICKER: { value: string; label: string }[] = [
  { value: '"Open Sans", sans-serif', label: 'Open Sans' },
  { value: 'Roboto, sans-serif', label: 'Roboto' },
  { value: 'Lato, sans-serif', label: 'Lato' },
  { value: 'Montserrat, sans-serif', label: 'Montserrat' },
  { value: 'Poppins, sans-serif', label: 'Poppins' },
  { value: 'Merriweather, serif', label: 'Merriweather' },
  { value: 'Oswald, sans-serif', label: 'Oswald' },
  { value: '"Source Sans 3", sans-serif', label: 'Source Sans 3' },
  { value: 'Inter, sans-serif', label: 'Inter' },
  { value: 'Nunito, sans-serif', label: 'Nunito' },
  { value: 'Raleway, sans-serif', label: 'Raleway' },
  { value: 'Work Sans, sans-serif', label: 'Work Sans' },
  { value: 'Ubuntu, sans-serif', label: 'Ubuntu' },
  { value: 'Playfair Display, serif', label: 'Playfair Display' },
  { value: 'PT Sans, sans-serif', label: 'PT Sans' },
];

const MAX_UNDO_HISTORY = 50;

export const EmailTemplateDesignEditor = forwardRef<EmailTemplateDesignEditorHandle, EmailTemplateDesignEditorProps>(function EmailTemplateDesignEditor({
  previewHtml,
  onHtmlChange,
  resetKey = 0,
  onUndoAvailability,
}, ref) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [selected, setSelected] = useState<SelectedElementInfo | null>(null);
  const [panelText, setPanelText] = useState('');
  const [panelStyle, setPanelStyle] = useState<SelectedElementInfo['style']>({});
  const [panelImgSrc, setPanelImgSrc] = useState('');
  const [panelImgAlt, setPanelImgAlt] = useState('');
  const [panelLinkHref, setPanelLinkHref] = useState('');
  const [panelLinkTarget, setPanelLinkTarget] = useState('');
  const [showFontPickerModal, setShowFontPickerModal] = useState(false);
  const [iframeContentHeight, setIframeContentHeight] = useState<number | null>(null);
  const lastSerializedRef = useRef<string>('');
  const lastResetKeyRef = useRef(resetKey);
  const historyRef = useRef<string[]>([]);
  const isUndoRestoreRef = useRef(false);

  // Only update iframe when resetKey changes (e.g. switching from Code to Design). Do NOT update when previewHtml
  // changes from our own design-serialized updates, or we replace the iframe document and lose lastSelected, so
  // panel edits (design-set-text, design-set-link, etc.) stop working.
  const [iframeBodyContent, setIframeBodyContent] = useState(() => extractBodyContent(previewHtml));
  useEffect(() => {
    if (resetKey !== lastResetKeyRef.current) {
      lastResetKeyRef.current = resetKey;
      setIframeBodyContent(extractBodyContent(previewHtml));
      setIframeContentHeight(null); // reset so we get fresh height from new doc
      historyRef.current = [];
      onUndoAvailability?.(false);
    }
  }, [resetKey, previewHtml, onUndoAvailability]);

  const undo = useCallback(() => {
    if (historyRef.current.length === 0) return;
    const prev = historyRef.current.pop();
    if (prev == null) return;
    isUndoRestoreRef.current = true;
    lastSerializedRef.current = prev;
    setIframeBodyContent(prev);
    onHtmlChange(prev);
    onUndoAvailability?.(historyRef.current.length > 0);
  }, [onHtmlChange, onUndoAvailability]);

  useImperativeHandle(ref, () => ({
    undo,
    canUndo: () => historyRef.current.length > 0,
  }), [undo]);

  const designDoc = buildDesignDocument(iframeBodyContent);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const d = event.data;
      if (!d || typeof d !== 'object') return;
      if (d.type === 'design-select') {
        if (d.element) {
          setSelected(d.element);
          setPanelText(d.element.textContent || '');
          setPanelStyle(d.element.style || {});
          setPanelImgSrc(d.element.src ?? '');
          setPanelImgAlt(d.element.alt ?? '');
          setPanelLinkHref(d.element.href ?? '');
          setPanelLinkTarget(d.element.target ?? '');
        } else {
          setSelected(null);
          setPanelText('');
          setPanelStyle({});
          setPanelImgSrc('');
          setPanelImgAlt('');
          setPanelLinkHref('');
          setPanelLinkTarget('');
        }
      } else if (d.type === 'design-serialized' && d.html != null) {
        if (isUndoRestoreRef.current) {
          isUndoRestoreRef.current = false;
          lastSerializedRef.current = d.html;
        } else if (d.html !== lastSerializedRef.current) {
          if (lastSerializedRef.current !== '') {
            historyRef.current.push(lastSerializedRef.current);
            if (historyRef.current.length > MAX_UNDO_HISTORY) historyRef.current.shift();
            onUndoAvailability?.(true);
          }
          lastSerializedRef.current = d.html;
        }
        if (typeof d.bodyHeight === 'number' && d.bodyHeight > 0) setIframeContentHeight(d.bodyHeight);
        onHtmlChange(d.html);
      } else if (d.type === 'design-content-height' && typeof d.bodyHeight === 'number' && d.bodyHeight > 0) {
        setIframeContentHeight(d.bodyHeight);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onHtmlChange, onUndoAvailability]);

  const applyText = useCallback(() => {
    if (!selected || selected.tagName === 'img' || iframeRef.current?.contentWindow == null) return;
    iframeRef.current.contentWindow.postMessage({ type: 'design-set-text', value: panelText }, '*');
  }, [selected, panelText]);

  const applyStyle = useCallback((partial: Partial<SelectedElementInfo['style']>) => {
    if (!selected || iframeRef.current?.contentWindow == null) return;
    const next = { ...panelStyle, ...partial };
    setPanelStyle(next);
    iframeRef.current.contentWindow.postMessage({ type: 'design-set-style', style: next }, '*');
  }, [selected, panelStyle]);

  const applyImg = useCallback(() => {
    if (!selected || selected.tagName !== 'img' || iframeRef.current?.contentWindow == null) return;
    iframeRef.current.contentWindow.postMessage({
      type: 'design-set-img',
      src: panelImgSrc,
      alt: panelImgAlt,
      width: panelStyle.width,
      height: panelStyle.height,
      borderRadius: panelStyle.borderRadius,
    }, '*');
  }, [selected, panelImgSrc, panelImgAlt, panelStyle.width, panelStyle.height, panelStyle.borderRadius]);

  const applyLink = useCallback(() => {
    if (!selected || selected.tagName !== 'a' || iframeRef.current?.contentWindow == null) return;
    const win = iframeRef.current.contentWindow;
    win.postMessage(
      { type: 'design-set-link', href: panelLinkHref, target: panelLinkTarget ? '_blank' : '' },
      '*'
    );
    // Force a fresh serialization after the iframe has applied the link so the parent always gets updated HTML
    requestAnimationFrame(() => {
      win.postMessage({ type: 'design-get-serialized' }, '*');
    });
  }, [selected, panelLinkHref, panelLinkTarget]);

  const applyFormat = useCallback(
    (format: 'bold' | 'italic' | 'underline' | 'code') => {
      if (!selected || selected.tagName === 'img' || iframeRef.current?.contentWindow == null) return;
      // Focus iframe so execCommand runs in the right document (bold/italic/underline require document focus)
      const win = iframeRef.current.contentWindow;
      win.focus();
      requestAnimationFrame(() => {
        win.postMessage({ type: 'design-format', format }, '*');
      });
    },
    [selected]
  );

  const removeElement = useCallback(() => {
    if (!selected || iframeRef.current?.contentWindow == null) return;
    iframeRef.current.contentWindow.postMessage({ type: 'design-remove' }, '*');
  }, [selected]);

  const parsePx = (s: string | undefined): string => {
    if (s == null || s === '') return '';
    return String(s).replace(/px$/i, '').trim();
  };
  const handlePxChange = (field: 'width' | 'height' | 'borderRadius', e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
    const next = v === '' ? '' : v + 'px';
    setPanelStyle((s) => ({ ...s, [field]: next }));
  };

  const pendingInsertRef = useRef<string | null>(null);
  const handlePaletteDragStart = useCallback((e: React.DragEvent, item: (typeof PALETTE_ITEMS)[0]) => {
    pendingInsertRef.current = item.html;
    e.dataTransfer.setData('application/x-governate-palette', item.html);
    e.dataTransfer.effectAllowed = 'copy';
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ type: 'design-palette-drag-start', html: item.html }, '*');
    }
  }, []);
  const handlePaletteDragEnd = useCallback(() => {
    pendingInsertRef.current = null;
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ type: 'design-palette-drag-end' }, '*');
    }
  }, []);

  const previewMinHeight = 'calc(100vh - 14rem)';
  return (
    <div className="flex gap-4 flex-1 min-h-0">
      {/* Left: element palette */}
      <div className="w-44 flex-shrink-0 flex flex-col bg-white dark:bg-gray-800/80 border-r border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Add elements</h3>
        </div>
        <div className="p-2 flex flex-col gap-1 overflow-y-auto flex-1 min-h-0">
          {PALETTE_ITEMS.map((item) => (
            <div
              key={item.id}
              draggable
              onDragStart={(e) => handlePaletteDragStart(e, item)}
              onDragEnd={handlePaletteDragEnd}
              className="flex items-center gap-2 px-3 py-2 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 cursor-grab active:cursor-grabbing hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-700/50 text-sm"
            >
              <span className="text-gray-400 dark:text-gray-500">{item.icon}</span>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
        <div className="p-2 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <p className="text-xs text-gray-500 dark:text-gray-400">Alt + ↑ ↓ ← → to move</p>
        </div>
      </div>
      {/* Center: preview */}
      <div className="flex-1 min-w-0 flex flex-col min-h-0 overflow-visible">
        <div
          className="flex flex-col w-full"
          style={{
            minHeight: previewMinHeight,
            height: iframeContentHeight ?? undefined,
          }}
        >
          <iframe
            ref={iframeRef}
            srcDoc={designDoc}
            title="Email design canvas"
            className="w-full border-0 bg-white dark:bg-gray-900"
            style={{ height: iframeContentHeight ? `${iframeContentHeight}px` : previewMinHeight, minHeight: previewMinHeight }}
            key={resetKey}
            sandbox="allow-same-origin allow-scripts"
          />
        </div>
      </div>
      <div className="w-72 flex-shrink-0 flex flex-col min-h-0 bg-white dark:bg-gray-800/80 overflow-y-auto">
        {selected ? (
          <>
          <div className="p-4 flex-shrink-0 bg-white dark:bg-gray-800 flex items-start justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Edit element</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 capitalize">{selected.tagName}</p>
            </div>
            <button
              type="button"
              onClick={removeElement}
              className="p-2 rounded text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
              title="Remove element"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <div className="p-4 space-y-4 overflow-y-auto flex-1 min-h-0">
            {selected.tagName === 'img' ? (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Image URL</label>
                  <input
                    type="text"
                    value={panelImgSrc}
                    onChange={(e) => setPanelImgSrc(e.target.value)}
                    onBlur={applyImg}
                    className="w-full px-2 py-1.5 text-sm rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Alt text</label>
                  <input
                    type="text"
                    value={panelImgAlt}
                    onChange={(e) => setPanelImgAlt(e.target.value)}
                    onBlur={applyImg}
                    className="w-full px-2 py-1.5 text-sm rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                    placeholder="Description"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Width</label>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={parsePx(panelStyle.width)}
                        onChange={(e) => handlePxChange('width', e)}
                        onBlur={applyImg}
                        className="w-full px-2 py-1.5 text-sm rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                        placeholder="0"
                      />
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">px</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Height</label>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={parsePx(panelStyle.height)}
                        onChange={(e) => handlePxChange('height', e)}
                        onBlur={applyImg}
                        className="w-full px-2 py-1.5 text-sm rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                        placeholder="0"
                      />
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">px</span>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Border radius</label>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={parsePx(panelStyle.borderRadius)}
                      onChange={(e) => handlePxChange('borderRadius', e)}
                      onBlur={applyImg}
                      className="w-full px-2 py-1.5 text-sm rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                      placeholder="0"
                    />
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">px</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  You can set a default logo for all templates by updating your company in{' '}
                  <Link
                    to="/settings"
                    onClick={() => localStorage.setItem('settings_activeTab', 'company')}
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Settings → Company
                  </Link>
                  . You can still override it here for this template.
                </p>
              </>
            ) : (
              <>
                {selected.tagName === 'a' && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Link URL</label>
                      <input
                        type="text"
                        value={panelLinkHref}
                        onChange={(e) => setPanelLinkHref(e.target.value)}
                        onBlur={applyLink}
                        className="w-full px-2 py-1.5 text-sm rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                        placeholder="https://..."
                      />
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={panelLinkTarget === '_blank'}
                        onChange={(e) => {
                          const newTarget = e.target.checked ? '_blank' : '';
                          setPanelLinkTarget(newTarget);
                          if (iframeRef.current?.contentWindow) {
                            iframeRef.current.contentWindow.postMessage(
                              { type: 'design-set-link', href: panelLinkHref, target: newTarget },
                              '*'
                            );
                          }
                        }}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Open in new tab</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Width</label>
                        <div className="flex items-center gap-1.5">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={parsePx(panelStyle.width)}
                            onChange={(e) => handlePxChange('width', e)}
                            onBlur={() => applyStyle({})}
                            className="w-full px-2 py-1.5 text-sm rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                            placeholder="0"
                          />
                          <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">px</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Height</label>
                        <div className="flex items-center gap-1.5">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={parsePx(panelStyle.height)}
                            onChange={(e) => handlePxChange('height', e)}
                            onBlur={() => applyStyle({})}
                            className="w-full px-2 py-1.5 text-sm rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                            placeholder="0"
                          />
                          <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">px</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Border radius</label>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={parsePx(panelStyle.borderRadius)}
                          onChange={(e) => handlePxChange('borderRadius', e)}
                          onBlur={() => applyStyle({})}
                          className="w-full px-2 py-1.5 text-sm rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                          placeholder="0"
                        />
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">px</span>
                      </div>
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Text</label>
                  <textarea
                    value={panelText}
                    onChange={(e) => setPanelText(e.target.value)}
                    onBlur={applyText}
                    rows={3}
                    className="w-full px-2 py-1.5 text-sm rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                    placeholder="Content"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Format</label>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => applyFormat('bold')}
                      className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white"
                      title="Bold"
                    >
                      <Bold className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => applyFormat('italic')}
                      className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white"
                      title="Italic"
                    >
                      <Italic className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => applyFormat('underline')}
                      className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white"
                      title="Underline"
                    >
                      <Underline className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => applyFormat('code')}
                      className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white"
                      title="Code"
                    >
                      <Code className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Font size</label>
                  <input
                    type="text"
                    value={panelStyle.fontSize || ''}
                    onChange={(e) => setPanelStyle((s) => ({ ...s, fontSize: e.target.value }))}
                    onBlur={() => applyStyle({ fontSize: panelStyle.fontSize })}
                    className="w-full px-2 py-1.5 text-sm rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                    placeholder="e.g. 16px"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Text color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={panelStyle.color || ''}
                      onChange={(e) => setPanelStyle((s) => ({ ...s, color: e.target.value }))}
                      onBlur={() => applyStyle({ color: panelStyle.color })}
                      className="flex-1 px-2 py-1.5 text-sm rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                      placeholder="e.g. #333"
                    />
                    <input
                      type="color"
                      value={panelStyle.color && /^#[0-9A-Fa-f]{6}$/.test(panelStyle.color) ? panelStyle.color : '#000000'}
                      onChange={(e) => {
                        const v = e.target.value;
                        setPanelStyle((s) => ({ ...s, color: v }));
                        applyStyle({ color: v });
                      }}
                      className="w-8 h-8 rounded cursor-pointer"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Background color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={panelStyle.backgroundColor || ''}
                      onChange={(e) => setPanelStyle((s) => ({ ...s, backgroundColor: e.target.value }))}
                      onBlur={() => applyStyle({ backgroundColor: panelStyle.backgroundColor })}
                      className="flex-1 px-2 py-1.5 text-sm rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                      placeholder="e.g. #2563eb"
                    />
                    <input
                      type="color"
                      value={panelStyle.backgroundColor && /^#[0-9A-Fa-f]{6}$/.test(panelStyle.backgroundColor) ? panelStyle.backgroundColor : '#ffffff'}
                      onChange={(e) => {
                        const v = e.target.value;
                        setPanelStyle((s) => ({ ...s, backgroundColor: v }));
                        applyStyle({ backgroundColor: v });
                      }}
                      className="w-8 h-8 rounded cursor-pointer"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Font</label>
                  <button
                    type="button"
                    onClick={() => setShowFontPickerModal(true)}
                    className="w-full px-3 py-2 text-sm rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-left flex items-center justify-between"
                    style={{ fontFamily: panelStyle.fontFamily || SYSTEM_FONT }}
                  >
                    <span>
                      {GOOGLE_FONTS_PICKER.find((f) => f.value === panelStyle.fontFamily)?.label ||
                        FONT_OPTIONS.find((o) => o.value !== '__custom__' && o.value === panelStyle.fontFamily)?.label ||
                        (panelStyle.fontFamily ? panelStyle.fontFamily.split(',')[0].replace(/['"]/g, '').trim() || 'System default' : 'System default')}
                    </span>
                    <span className="text-gray-400">▼</span>
                  </button>
                </div>
              </>
            )}
          </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center p-6 min-h-0">
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">Click an item to customize</p>
          </div>
        )}
      </div>

      {showFontPickerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowFontPickerModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Choose a font</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Pick a font to use</p>
            </div>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Preview</p>
              <div
                className="min-h-[4rem] px-3 py-2 rounded border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white"
                style={{ fontFamily: panelStyle.fontFamily || SYSTEM_FONT, fontSize: panelStyle.fontSize || '1rem' }}
              >
                {panelText || 'Sample text'}
              </div>
            </div>
            <div className="p-3 overflow-y-auto flex-1 grid grid-cols-2 gap-1">
              {GOOGLE_FONTS_PICKER.map((font) => (
                <button
                  key={font.value}
                  type="button"
                  onClick={() => setPanelStyle((s) => ({ ...s, fontFamily: font.value }))}
                  className="text-left px-3 py-2 rounded text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                  style={{ fontFamily: font.value }}
                >
                  {font.label}
                </button>
              ))}
            </div>
            <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex gap-2">
              <button
                type="button"
                onClick={() => {
                  applyStyle({ fontFamily: panelStyle.fontFamily });
                  setShowFontPickerModal(false);
                }}
                className="flex-1 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
              >
                Done
              </button>
              <button type="button" onClick={() => setShowFontPickerModal(false)} className="py-2 px-4 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
